import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import swal from "sweetalert";
import Role from "../helpers/role";
import API from "../helpers/api";
import { showLoader, hideLoader } from "../Actions/PageLoaderAPI";
import "../App.css";
import "../Asset/css/dashboard.css";
import "../Asset/css/monthly-report-comments-redesign.css";

const mapDetail = (data) => {
  const days = (data.days || data.Days || []).map((d) => ({
    date: d.date || d.Date,
    isSelected: d.isSelected ?? d.IsSelected ?? false,
    status: d.status || d.Status || "",
    comments: d.comments ?? d.Comments ?? "",
    isLocked: d.isLocked ?? d.IsLocked ?? false,
  }));
  return {
    id: data.id ?? data.Id,
    crewId: data.crewId ?? data.CrewId ?? "",
    crewName: data.crewName ?? data.CrewName ?? "",
    gapFromDate: data.gapFromDate ?? data.GapFromDate,
    gapToDate: data.gapToDate ?? data.GapToDate,
    isEditable: data.isEditable ?? data.IsEditable ?? false,
    lockMessage: data.lockMessage ?? data.LockMessage ?? "",
    days,
  };
};

class MonthlyReportCommentEdit extends React.Component {
  constructor(props) {
    super(props);
    const user = JSON.parse(localStorage.getItem("currentUser") || "{}");
    this.state = {
      actionBy: user.email || "",
      detail: null,
      // { "YYYY-MM-DD": { selected: bool, comment: string } }
      dayState: {},
      bulkComment: "",
      loading: true,
      saving: false,
    };
  }

  componentDidMount() {
    const userRole = localStorage.getItem("UserRoles");
    if (userRole !== Role.staff) {
      this.props.history.replace("/pagenotfound");
      return;
    }
    this.props.dispatch({
      type: "SubBannerTitle",
      data: "Monthly Compliance Reports",
    });
    this.loadDetail();
  }

  getEntryId = () => {
    const { match } = this.props;
    return parseInt(match?.params?.id, 10);
  };

  loadDetail = () => {
    const id = this.getEntryId();
    if (!id) {
      swal("Error!", "Invalid compliance entry.", "warning");
      this.props.history.replace("/Home/Management/MonthlyReportComments");
      return;
    }

    this.setState({ loading: true });
    this.props.dispatch(showLoader());

    axios
      .post(API.GetComplianceEntryDetail, {
        id,
        actionBy: this.state.actionBy,
      })
      .then((response) => {
        const detail = mapDetail(response.data || {});
        const dayState = {};
        detail.days.forEach((d) => {
          const key = Moment(d.date).format("YYYY-MM-DD");
          dayState[key] = {
            selected: !!d.isSelected,
            comment: d.comments || "",
          };
        });
        this.setState({
          detail,
          dayState,
          loading: false,
        });
      })
      .catch((error) => {
        const apiMessage =
          (typeof error?.response?.data === "string" && error.response.data) ||
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Unable to load comment details.";
        swal("Error!", apiMessage, "warning").then(() => {
          this.props.history.replace("/Home/Management/MonthlyReportComments");
        });
      })
      .finally(() => {
        this.props.dispatch(hideLoader());
      });
  };

  isDayLocked = (dateKey) => {
    const day = this.state.detail?.days?.find(
      (d) => Moment(d.date).format("YYYY-MM-DD") === dateKey
    );
    return !!day?.isLocked;
  };

  toggleDate = (dateKey) => {
    if (!this.state.detail?.isEditable || this.isDayLocked(dateKey)) return;
    this.setState((prev) => {
      const current = prev.dayState[dateKey] || { selected: false, comment: "" };
      return {
        dayState: {
          ...prev.dayState,
          [dateKey]: {
            selected: !current.selected,
            comment: current.comment || "",
          },
        },
      };
    });
  };

  updateDayComment = (dateKey, comment) => {
    if (!this.state.detail?.isEditable || this.isDayLocked(dateKey)) return;
    this.setState((prev) => ({
      dayState: {
        ...prev.dayState,
        [dateKey]: {
          ...(prev.dayState[dateKey] || { selected: true, comment: "" }),
          selected: true,
          comment,
        },
      },
    }));
  };

  selectAll = () => {
    if (!this.state.detail?.isEditable) return;
    const dayState = { ...this.state.dayState };
    this.state.detail.days.forEach((d) => {
      if (d.isLocked) return;
      const key = Moment(d.date).format("YYYY-MM-DD");
      const existing = dayState[key] || {};
      dayState[key] = {
        selected: true,
        comment: existing.comment || "",
      };
    });
    this.setState({ dayState });
  };

  clearAll = () => {
    if (!this.state.detail?.isEditable) return;
    const dayState = { ...this.state.dayState };
    this.state.detail.days.forEach((d) => {
      if (d.isLocked) return;
      const key = Moment(d.date).format("YYYY-MM-DD");
      dayState[key] = { selected: false, comment: "" };
    });
    this.setState({ dayState, bulkComment: "" });
  };

  applyBulkComment = () => {
    if (!this.state.detail?.isEditable) return;
    const text = (this.state.bulkComment || "").trim();
    if (!text) {
      toast.error("Enter a comment to apply to selected dates.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
      });
      return;
    }

    const dayState = { ...this.state.dayState };
    let applied = 0;
    Object.keys(dayState).forEach((key) => {
      if (this.isDayLocked(key)) return;
      if (dayState[key]?.selected) {
        dayState[key] = { ...dayState[key], comment: text };
        applied += 1;
      }
    });

    if (applied === 0) {
      toast.error("Select at least one unlocked date first.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
      });
      return;
    }

    this.setState({ dayState });
    toast.success(`Comment applied to ${applied} selected date(s).`, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
    });
  };

  save = () => {
    const { detail, dayState, actionBy } = this.state;
    if (!detail?.isEditable) {
      swal(
        "Locked",
        detail?.lockMessage ||
          "This period is locked because the monthly report has already been generated.",
        "warning"
      );
      return;
    }

    // Only send unlocked days — API preserves locked days from DB.
    const dayComments = [];
    detail.days.forEach((d) => {
      if (d.isLocked) return;
      const key = Moment(d.date).format("YYYY-MM-DD");
      const item = dayState[key];
      if (item?.selected) {
        dayComments.push({
          date: key,
          comments: (item.comment || "").trim(),
        });
      }
    });

    const missing = dayComments.find((d) => !d.comments);
    if (missing) {
      toast.error(
        `Comment is required for ${Moment(missing.date).format("D MMM YYYY")}.`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
        }
      );
      return;
    }

    this.setState({ saving: true });
    this.props.dispatch(showLoader());

    axios
      .post(API.SaveComplianceComment, {
        id: detail.id,
        actionBy,
        dayComments,
      })
      .then((response) => {
        const result = response.data || {};
        const success = result.status ?? result.Status;
        const message =
          result.message ||
          result.Message ||
          (success ? "Comments saved successfully." : "Unable to save.");

        if (success) {
          toast.success(message, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: true,
          });
          this.props.history.push("/Home/Management/MonthlyReportComments");
        } else {
          swal("Error!", message, "warning");
          this.setState({ saving: false });
        }
      })
      .catch((error) => {
        const apiMessage =
          (typeof error?.response?.data === "string" && error.response.data) ||
          error?.response?.data?.message ||
          error?.response?.data?.Message ||
          "Something went wrong while saving comments.";
        swal("Error!", apiMessage, "warning");
        this.setState({ saving: false });
      })
      .finally(() => {
        this.props.dispatch(hideLoader());
      });
  };

  BacktoPrevious = () => {
    this.props.history.push("/Home/Management/MonthlyReportComments");
  };

  render() {
    const { detail, dayState, loading, saving } = this.state;

    if (loading || !detail) {
      return (
        <div className="pt-3">
          <div className="row px-3 pb-3">
            <button
              className="btn btn-primary text-uppercase txtside-btn txtht-btn blue"
              onClick={this.BacktoPrevious}
            >
              <div style={{ position: "relative" }}>
                <span className="backarr">&larr;</span>
                <span style={{ marginLeft: "18px" }}>Back</span>
              </div>
            </button>
          </div>
        </div>
      );
    }

    const editable = detail.isEditable;

    return (
      <div className="pt-3 mrc-redesign">
        <div className="mrc-page-container">
        <div className="mrc-page-back">
          <button
            className="btn btn-primary text-uppercase txtside-btn txtht-btn blue"
            onClick={this.BacktoPrevious}
          >
            <div style={{ position: "relative" }}>
              <span className="backarr">&larr;</span>
              <span style={{ marginLeft: "18px" }}>Back</span>
            </div>
          </button>
        </div>

        <div className="mrc-page-card">
            <div className="card MM-card-header mb-0">
              <div
                className="card-body flex"
                style={{
                  padding: "0rem",
                  backgroundColor: "#1a3464",
                  color: "white",
                }}
              >
                <h5 className="card-title mrc-card-title">
                  <span className="mrc-card-title-icon mrc-add-comment-icon" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 4.75h14A2.25 2.25 0 0 1 21.25 7v8.5A2.25 2.25 0 0 1 19 17.75H9.2L5.6 20.7a.75.75 0 0 1-1.2-.6V17.75A2.25 2.25 0 0 1 2.75 15.5V7A2.25 2.25 0 0 1 5 4.75z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.5 9h9M7.5 12h7M7.5 15h5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="mrc-card-title-text">
                    Add Comments – {detail.crewName} ({detail.crewId})
                  </span>
                </h5>
              </div>
            </div>

            <div className="panel-box search-filter-box mb-0 mrc-edit-panel">
              {detail.lockMessage ? (
                <div className="alert alert-warning m-3 mb-0" role="alert">
                  {detail.lockMessage}
                </div>
              ) : null}

              <div className="mrc-edit-info-bar">
                <div className="mrc-edit-info-item">
                  <div className="mrc-field-icon">
                    <i className="fa fa-id-badge" aria-hidden="true" />
                  </div>
                  <div className="mrc-field-body">
                    <p className="mrc-field-label">Crew ID</p>
                    <p className="mrc-field-value">{detail.crewId || "-"}</p>
                  </div>
                </div>
                <div className="mrc-edit-info-item">
                  <div className="mrc-field-icon">
                    <i className="fa fa-user-o" aria-hidden="true" />
                  </div>
                  <div className="mrc-field-body">
                    <p className="mrc-field-label">Crew Name</p>
                    <p className="mrc-field-value">{detail.crewName || "-"}</p>
                  </div>
                </div>
                <div className="mrc-edit-info-item">
                  <div className="mrc-field-icon">
                    <i className="fa fa-calendar" aria-hidden="true" />
                  </div>
                  <div className="mrc-field-body">
                    <p className="mrc-field-label">Missing Period</p>
                    <p className="mrc-field-value">
                      {Moment(detail.gapFromDate).format("D MMM YYYY")} to{" "}
                      {Moment(detail.gapToDate).format("D MMM YYYY")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mrc-edit-body">
              <div className="mrc-step-block">
                <label className="form-label mrc-step-title">
                  <span className="mrc-step-icon" aria-hidden="true">
                    <i className="fa fa-calendar-check-o" aria-hidden="true" />
                  </span>
                  <span>
                    1. Select the dates and enter a comment for each selected
                    date.
                  </span>
                </label>
                {editable ? (
                  <div className="mrc-outline-btns">
                    <button
                      type="button"
                      className="btn text-uppercase"
                      onClick={this.selectAll}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn text-uppercase"
                      onClick={this.clearAll}
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
              </div>

              {editable ? (
                <div className="mrc-step-block">
                  <label className="form-label mrc-step-title">
                    <span className="mrc-step-icon mrc-step-comment-icon" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 4.75h14A2.25 2.25 0 0 1 21.25 7v8.5A2.25 2.25 0 0 1 19 17.75H9.2L5.6 20.7a.75.75 0 0 1-1.2-.6V17.75A2.25 2.25 0 0 1 2.75 15.5V7A2.25 2.25 0 0 1 5 4.75z"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M7.5 9h9M7.5 12h7M7.5 15h5"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span>
                      2. (Optional) Add the same comment for all selected dates.
                    </span>
                  </label>
                  <div className="mrc-bulk-row">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter a comment to apply to all selected dates"
                      value={this.state.bulkComment || ""}
                      onChange={(e) =>
                        this.setState({ bulkComment: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-primary text-uppercase txtside-btn txtht-btn blue"
                      onClick={this.applyBulkComment}
                    >
                      Apply to Selected
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="mb-3 mrc-days-wrap">
                {detail.days.map((day) => {
                  const key = Moment(day.date).format("YYYY-MM-DD");
                  const state = dayState[key] || {
                    selected: false,
                    comment: "",
                  };
                  const dayEditable = editable && !day.isLocked;
                  const rowClass = [
                    "mrc-day-row",
                    state.selected ? "selected" : "",
                    day.isLocked ? "locked" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <div key={key} className={rowClass}>
                      <label
                        className="mrc-day-check"
                        style={{
                          cursor: dayEditable ? "pointer" : "default",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!state.selected}
                          disabled={!dayEditable}
                          onChange={() => this.toggleDate(key)}
                        />
                        <i
                          className="fa fa-calendar mrc-day-cal-icon"
                          aria-hidden="true"
                        />
                        <strong>
                          {Moment(day.date).format("D MMM YYYY")}
                        </strong>
                        {day.isLocked ? (
                          <span
                            className="text-muted"
                            style={{ fontSize: "12px", fontWeight: 400 }}
                          >
                            (Locked)
                          </span>
                        ) : null}
                      </label>
                      <div className="mrc-day-input-wrap">
                        {state.selected ? (
                          <input
                            type="text"
                            className="form-control mrc-day-comment-input"
                            placeholder={`Comment for ${Moment(day.date).format(
                              "D MMM"
                            )}`}
                            value={state.comment || ""}
                            disabled={!dayEditable}
                            onChange={(e) =>
                              this.updateDayComment(key, e.target.value)
                            }
                          />
                        ) : (
                          <div className="mrc-day-pending-wrap">
                            <span className="mrc-day-pending-text">
                              {day.isLocked
                                ? "Locked by monthly report"
                                : "Not selected (will remain Pending)"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mrc-info-box mb-3">
                <p className="mrc-info-box-title">
                  <i className="fa fa-info-circle" aria-hidden="true" />
                  <strong>Important:</strong>
                </p>
                <ul className="mrc-info-list">
                  <li>Each selected date can have its own comment.</li>
                  <li>
                    Use &quot;Apply to Selected&quot; to apply the same comment to
                    all selected dates.
                  </li>
                  <li>Unselected dates will remain as Pending.</li>
                  <li>
                    Contiguous dates with the same comment will be stored as a
                    single range.
                  </li>
                </ul>
              </div>

              <div className="mrc-edit-footer">
                <button
                  className="btn text-uppercase txtside-btn txtht-btn btn-cancel"
                  type="button"
                  onClick={this.BacktoPrevious}
                  disabled={saving}
                >
                  Cancel
                </button>
                {editable ? (
                  <button
                    className="btn btn-primary text-uppercase txtside-btn txtht-btn blue"
                    type="button"
                    onClick={this.save}
                    disabled={saving}
                  >
                    Save
                  </button>
                ) : null}
              </div>
              </div>
        </div>
        </div>
      </div>
      </div>
    );
  }
}

export default connect()(withRouter(MonthlyReportCommentEdit));
